import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Star, Check } from "lucide-react";
import { getReviews } from "@/lib/content";
import type { Review } from "@/types/content";

const ReviewsSection = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    getReviews(setReviews).then(setReviews);
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (reviews.length === 0) return null;

  // Triple the reviews for a truly seamless loop even on ultra-wide screens
  const multipliedReviews = [...reviews, ...reviews, ...reviews];

  return (
    <section id="reviews" className="py-20 md:py-28 bg-[#FAF8F5] overflow-hidden border-t border-border/40">
      <div className="container px-4 md:px-8 mb-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="space-y-3">
            <p className="text-muted-foreground font-body font-light tracking-[0.25em] uppercase text-[10px] md:text-xs">CLIENT NOTES</p>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light text-foreground uppercase tracking-[0.3em]">
              WHAT THEY SAY
            </h2>
          </div>
          <p className="text-muted-foreground text-xs sm:text-sm font-body font-light tracking-wide uppercase max-w-sm">
            Read our customer reviews on comfort, sizing, and material quality.
          </p>
        </div>
      </div>

      {/* Marquee Row */}
      <div className="relative flex whitespace-nowrap overflow-hidden max-w-full select-none py-4">
        <motion.div
          className="flex gap-4 sm:gap-6 px-3"
          animate={{
            x: ["0%", "-33.33%"],
          }}
          transition={{
            x: {
              repeat: Infinity,
              repeatType: "loop",
              duration: isMobile ? 35 : 28, // Slower for premium ease of reading
              ease: "linear",
            },
          }}
        >
          {multipliedReviews.map((review, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 w-[260px] sm:w-[300px] md:w-[380px] bg-[#FAF8F5] border border-border p-6 md:p-8 rounded-none transition-colors hover:border-foreground/45 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center gap-3 mb-4 md:mb-6">
                  <div className="w-8 h-8 md:w-10 h-10 rounded-none border border-border shrink-0 flex items-center justify-center bg-[#FAF8F5] text-foreground text-xs font-body font-light">
                    {review.image ? (
                      <img
                        src={review.image}
                        alt={review.name}
                        width="48"
                        height="48"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      review.name.charAt(0)
                    )}
                  </div>
                  <div className="overflow-hidden">
                    <h3 className="text-xs font-body font-light text-foreground uppercase tracking-widest truncate">{review.name}</h3>
                    <div className="flex items-center gap-1 mt-0.5">
                      <span className="text-[8px] md:text-[9px] font-body font-light text-muted-foreground uppercase tracking-[0.15em]">Verified Buyer</span>
                    </div>
                  </div>
                </div>
                <p className="text-foreground/80 text-xs sm:text-sm font-body font-light leading-relaxed italic whitespace-normal">
                  "{review.content}"
                </p>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Gradient overlays for smooth fade edges */}
        <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-[#FAF8F5] via-[#FAF8F5]/80 to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-[#FAF8F5] via-[#FAF8F5]/80 to-transparent z-10 pointer-events-none" />
      </div>
    </section>
  );
};

export default ReviewsSection;
